import { getUserDetails } from '../api/userApi';
import { getBillingStatus } from '../api/billingApi';

/**
 * AppStateManager - Centralized state management for shared application data
 * Prevents duplicate API calls by maintaining a single source of truth
 */
class AppStateManager {
  constructor() {
    this._userDetails = null;
    this._userDetailsPromise = null;
    this._subscriptionStatus = null;
    this._subscriptionStatusPromise = null;
    this._listeners = {
      userDetails: [],
      subscriptionStatus: []
    };
  }

  /**
   * Get user details - returns cached data if available or fetches new data
   * @returns {Promise<Object>} User details object
   */
  async getUserDetails() {
    // Return cached data if available
    if (this._userDetails) {
      return this._userDetails;
    }

    // If a fetch is already in progress, return that promise
    if (this._userDetailsPromise) {
      return this._userDetailsPromise;
    }

    // Start a new fetch
    this._userDetailsPromise = getUserDetails()
      .then(result => {
        this._userDetails = result;
        this._userDetailsPromise = null;
        // Notify listeners
        this._notifyListeners('userDetails', result);
        return result;
      })
      .catch(error => {
        this._userDetailsPromise = null;
        throw error;
      });

    return this._userDetailsPromise;
  }

  /**
   * Get subscription status - returns cached data if available or fetches new data
   * @returns {Promise<Object>} Subscription status object
   */
  async getSubscriptionStatus() {
    // Return cached data if available
    if (this._subscriptionStatus) {
      return this._subscriptionStatus;
    }

    // If a fetch is already in progress, return that promise
    if (this._subscriptionStatusPromise) {
      return this._subscriptionStatusPromise;
    }

    // Start a new fetch
    this._subscriptionStatusPromise = getBillingStatus()
      .then(result => {
        this._subscriptionStatus = result;
        this._subscriptionStatusPromise = null;
        // Notify listeners
        this._notifyListeners('subscriptionStatus', result);
        return result;
      })
      .catch(error => {
        this._subscriptionStatusPromise = null;
        throw error;
      });

    return this._subscriptionStatusPromise;
  }

  /**
   * Reset the cached data (use when logging out or switching users)
   */
  resetCache() {
    this._userDetails = null;
    this._subscriptionStatus = null;
  }

  /**
   * Listen for changes to a specific data type
   * @param {string} dataType - The type of data to listen for ('userDetails' or 'subscriptionStatus')
   * @param {Function} listener - Callback function when data changes
   * @returns {Function} Unsubscribe function
   */
  subscribe(dataType, listener) {
    if (!this._listeners[dataType]) {
      this._listeners[dataType] = [];
    }

    this._listeners[dataType].push(listener);

    // Return unsubscribe function
    return () => {
      this._listeners[dataType] = this._listeners[dataType].filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners of a data change
   * @private
   */
  _notifyListeners(dataType, data) {
    if (this._listeners[dataType]) {
      this._listeners[dataType].forEach(listener => listener(data));
    }
  }
}

// Create and export a singleton instance
const appStateManager = new AppStateManager();
export default appStateManager; 